import type { Fact } from "../../api/types";

/**
 * The two addresses a request carries (#805) — the pure part.
 *
 * They answer different questions. `customer.billing_address` is where the invoice goes;
 * `event.event_address` is where the food has to arrive. A company that orders from its head
 * office for a party at a rented hall has two different ones, and treating either as a stand-in
 * for the other sends an invoice to a venue or a delivery to an accounts department. So they are
 * read independently here and neither is ever consulted to fill the other in.
 *
 * Both come from the facts the system already holds. Nothing is stored a second time: this is a
 * reading of the request's own extracted state, and correcting an address stays where every other
 * fact is corrected.
 */

export type CustomerAddressKind = "billing" | "event";

/**
 * A fact status only counts as an answer when the system actually decided. `unknown`,
 * `ambiguous` and `conflicting` mean it could not — showing one of those as the delivery address
 * would put a value the system does not stand behind in front of an operator as if it did.
 */
const ANSWERED_STATUSES: ReadonlySet<string> = new Set([
  "extracted",
  "confirmed",
  "externally_resolved",
  "curated_venue_resolved",
  "manually_overridden",
]);

export const CUSTOMER_ADDRESS_SOURCES: ReadonlyArray<{
  kind: CustomerAddressKind;
  fieldPath: string;
  labelKey: string;
}> = [
  { kind: "billing", fieldPath: "customer.billing_address", labelKey: "ws.customer.billingAddress" },
  { kind: "event", fieldPath: "event.event_address", labelKey: "ws.customer.eventAddress" },
];

export interface CustomerAddressView {
  kind: CustomerAddressKind;
  fieldPath: string;
  labelKey: string;
  /** The address as the system holds it, one entry per line. Empty when there is no answer. */
  lines: string[];
  /** i18n key for the status of the fact behind it, or null when no fact exists at all. */
  statusKey: string | null;
  /**
   * True when a fact exists but its status says the system could not decide. The operator is told
   * that much rather than being shown nothing, because "nobody wrote one" and "we cannot tell
   * which of two it is" call for different next steps.
   */
  undecided: boolean;
}

function addressLines(value: unknown): string[] {
  if (typeof value !== "string") return [];
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

/** Both addresses, in a fixed order, each read only from its own fact. */
export function customerAddressViews(facts: readonly Fact[]): CustomerAddressView[] {
  return CUSTOMER_ADDRESS_SOURCES.map(({ kind, fieldPath, labelKey }) => {
    const fact = facts.find((candidate) => candidate.fieldPath === fieldPath) ?? null;
    const answered = fact !== null && ANSWERED_STATUSES.has(fact.status);
    const lines = answered ? addressLines(fact?.value) : [];
    return {
      kind,
      fieldPath,
      labelKey,
      lines,
      statusKey: fact ? `status.fact.${fact.status}` : null,
      undecided: fact !== null && !answered,
    };
  });
}
